import './styles.css';
import { locale, t } from './i18n/index.js';
import { lookupCode } from './inbox-api.js';
import { renderInboxPage } from './inbox-page.js';
import { renderReceiverPage } from './receiver-page.js';
import { buildInboxPath } from './route.js';
import { renderSendToInboxPage } from './send-to-inbox-page.js';
import { parseRoute } from './route.js';
import { renderSenderPage } from './sender-page.js';
import { clearShareQuery, parseShareQuery, receivePathFromSharedText, takeSharedFile } from './share-target.js';
import { el } from './ui/dom.js';
import { registerServiceWorker } from './service-worker.js';
import { setupInstallBanner } from './ui/install-banner.js';
import { createHeader, mountStarfield } from './ui/shell.js';

const renderNotFound = (root: HTMLElement): void => {
  const link = el('a', { className: 'button', href: '/', text: t('notFound.action') });
  root.append(
    el('main', { className: 'page' }, [
      createHeader(t('notFound.tagline')),
      el('section', { className: 'card' }, [el('p', { className: 'card__text', text: t('notFound.text') }), link]),
    ]),
  );
};

/** Resolves what the OS share sheet handed over: a file to send, a link or PIN to open, or nothing. */
const resolveShare = async (): Promise<File | null> => {
  const intent = parseShareQuery(window.location.search);
  if (intent === null) {
    return null;
  }
  clearShareQuery();
  if (intent.kind === 'file') {
    return takeSharedFile();
  }
  const path = receivePathFromSharedText(intent.payload, window.location.origin);
  if (path !== null) {
    window.location.replace(path);
  }
  return null;
};

const bootstrap = async (): Promise<void> => {
  document.documentElement.lang = locale;
  document.title = t('app.title');
  const root = document.getElementById('app');
  if (root === null) {
    throw new Error('Root element #app is missing from index.html');
  }
  const route = parseRoute(window.location.pathname);
  switch (route.kind) {
    case 'send':
      renderSenderPage(root, await resolveShare());
      break;
    case 'receive': {
      // A typed or scanned 9-digit code may be a permanent inbox rather than a transfer PIN.
      if (route.target.kind === 'pin' && (await lookupCode(route.target.pin)) === 'inbox') {
        window.location.replace(buildInboxPath(route.target.pin));
        return;
      }
      renderReceiverPage(root, route.target);
      break;
    }
    case 'inbox':
      renderInboxPage(root);
      break;
    case 'send-to-inbox':
      renderSendToInboxPage(root, route.inbox);
      break;
    case 'not-found':
      renderNotFound(root);
      break;
  }
  mountStarfield(document.body);
  setupInstallBanner(document.body);
  registerServiceWorker().catch((error: unknown) => {
    throw new Error(`Service worker registration failed: ${String(error)}`);
  });
};

bootstrap().catch((error: unknown) => {
  throw new Error(`Failed to start the app: ${String(error)}`);
});
